import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Autocomplete, Box, Button, Chip, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ROUTE_STRING } from 'constants/RouteString';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import { useInterval } from '@tenant/utility/hooks/useInterval';
import useGetListPipelines from 'redux/slices/pipelines/hooks/useGetListPipelines';
import { PIPELINE_RUN_POLL_INTERVAL_MS, PIPELINE_RUN_STATUS_LABEL, PipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import { DELETED_APP_INSTANCE_MARKER, NEVER_RUN_STATUS_VALUE } from 'redux/slices/pipelines/constants/pipelineListNarrowing';
import { flattenPipelineGroups } from 'redux/slices/pipelines/helpers/flattenPipelineGroups';
import {
	compareAppInstanceNameValues,
	compareLatestRunStatusValues,
	comparePipelineTextValues,
	sortPipelineRowsByDefault
} from 'redux/slices/pipelines/helpers/comparePipelineRows';
import { buildAppInstanceFacetOptions, narrowPipelineRows } from 'redux/slices/pipelines/helpers/narrowPipelineRows';
import {
	fromGridSortModel,
	isPipelineListNarrowed,
	parsePipelineListViewState,
	serialisePipelineListViewState,
	toGridSortModel
} from 'redux/slices/pipelines/helpers/pipelineListUrlState';
import { hasNonTerminalPipelineRun } from 'redux/slices/pipelines/helpers/hasNonTerminalPipelineRun';
import PipelineRunStatusChip from '../Common/PipelineRunStatusChip';

// deploy-pipelines-list-payload: the ~190-request/~28s N+1 storm is gone. `GET /api/pipelines`
// now carries AppInstanceName (group), ContainerAppName and LatestRunStatus (per pipeline) —
// every column below binds straight off the single list payload. This removes:
//   - the bulk `useGetListAppInstance({ pageSize: 500 })` app-instances lookup (AC-8),
//   - the per-group `PipelineGroupContainerNames` invisible fetchers (AC-7),
//   - the per-row `PipelineLatestRunStatusCell` runs fetch (AC-9).
// Run-status freshness is kept via a single gated list-level refetch, not per-row polling —
// see the useInterval call below.
//
// pipelines-list-sort-and-narrow (Wave 1 of pipelines-list-findability) — the list gained a
// learnable order and the means to narrow it. FE-only: no BE change, no contract change, no new
// dependency, and still exactly one GET /api/pipelines per page open (AC-21/AC-22).
//   * a meaningful DEFAULT ORDER — App Instance name, then pipeline name (AC-1/AC-2), replacing
//     the server's AppInstanceId-GUID order, which is unpredictable to a human,
//   * SORTABLE columns with null semantics declared PER COLUMN, never left to the grid (AC-4..AC-8),
//   * an App Instance FACET and status QUICK-FILTERS, both binding to fields already on the row
//     (AC-10..AC-13), with `(deleted)` and never-run as first-class selectable values,
//   * narrowing state carried in the URL, so it survives the detail round trip AND is shareable
//     (AC-15/AC-16 — spec D3: the URL is the SINGLE source of truth; redux `filter: {}` is
//     deliberately NOT written to, because two sources of truth is how a list starts disagreeing
//     with its own address bar),
//   * a poller that no longer goes blind when a filter is active (AC-17/AC-18).
//
// Sorting is switched off PORTAL-WIDE (`sortable: false` occurs 41 times across this FE, and no
// list anywhere used `sortingMode`/`sortModel` before this one), so this file INTRODUCES a pattern
// rather than conforming to one. It is argued on the job to be done — a worklist needs an order —
// not on catching up to a house standard, because there is no such standard here to catch up to.
const PipelineTable = () => {
	const navigate = useNavigate();
	const nextTick = useNextTick();

	const { isLoading, refetch } = useGetListPipelines();
	const { list } = useSelector(s => s.pipeline);
	const groups = list ?? [];
	const totalPipelineCount = groups.reduce((sum, g) => sum + (g.Pipelines?.length ?? 0), 0);

	// AC-15/AC-16 — narrowing state lives in the URL and nowhere else (spec D3). This is what
	// replaces the old `useState('')` search term, which was thrown away on every navigation.
	const [searchParams, setSearchParams] = useSearchParams();
	const searchString = searchParams.toString();
	const viewState = useMemo(() => parsePipelineListViewState(searchString), [searchString]);

	// ⚠ `replace: true` IS LOAD-BEARING, NOT AN OPTIMISATION. Every keystroke and every facet click
	// rewrites this URL. Pushing them would bury the previous page under dozens of history
	// entries, so the back button would step backwards through the reader's own typing instead of
	// leaving the list — which would silently undo the back-navigation fix shipped in c9cc3bb0 (the
	// very fix AC-15 depends on). Replacing keeps exactly one history entry for the list, holding
	// the CURRENT narrowing, which is precisely what AC-15 asks to be restored.
	const updateViewState = useCallback(
		patch => {
			setSearchParams(serialisePipelineListViewState({ ...viewState, ...patch }), { replace: true });
		},
		[setSearchParams, viewState]
	);

	// AC-1/AC-2 — flatten every group's Pipelines into one row set (carrying the owning App
	// Instance's Id/Name straight from the payload, no client-side lookup), then impose the default
	// order. The GUID order the server returns never reaches the grid.
	const loadedRows = useMemo(() => sortPipelineRowsByDefault(flattenPipelineGroups(groups)), [groups]);

	// AC-11 — facet options derived from the loaded rows, so `(deleted)` is offered exactly when
	// some row is actually orphaned.
	//
	// The current selection is passed in as well (review LOW-1): a link shared per AC-16b can name an
	// App Instance whose last pipeline is gone, and an option list built from rows alone would leave
	// that selection unrepresentable — MUI warns, and the reader sees an empty list with no visible
	// cause. The helper's own comment carries the trade-off.
	const appInstanceOptions = useMemo(() => buildAppInstanceFacetOptions(loadedRows, viewState.appInstances), [loadedRows, viewState.appInstances]);

	// AC-13 — search AND facet AND status, composed by a pure predicate. The four-field search
	// itself is untouched, quirk included.
	const visibleRows = useMemo(() => narrowPipelineRows(loadedRows, viewState), [loadedRows, viewState]);

	const clearSearchTerm = async () => {
		updateViewState({ searchTerm: '' });
		await nextTick();
	};

	const onChangeSearch = e => {
		updateViewState({ searchTerm: e.target.value });
	};

	// AC-12 — a status quick-filter toggles: click to add, click again to remove. Never-run is one
	// of the options, not a special case bolted on beside them.
	const toggleStatus = value => {
		const current = viewState.statuses ?? [];
		const key = String(value);
		const next = current.some(v => String(v) === key) ? current.filter(v => String(v) !== key) : [...current, key];
		updateViewState({ statuses: next });
	};

	const isStatusSelected = value => (viewState.statuses ?? []).some(v => String(v) === String(value));

	// ---------------------------------------------------------------------------
	// AC-17/AC-18 — the gated list-level refetch, now FILTER-PROOF.
	// ---------------------------------------------------------------------------
	//
	// ⚠ THIS GATE READS `loadedRows`, NOT `visibleRows`. That is a DELIBERATE REVERSAL of
	// deploy-pipelines-list-payload AC-12, signed off by ducp on 2026-08-05 (spec D2), and the
	// shipped AC-12 carries a superseded-by annotation at its own end.
	//
	// DO NOT "restore" the visible-row gate. The comment that used to sit here called it deliberate,
	// and it was — but it was also wrong: gating on the VISIBLE rows made the poller filter-blind,
	// so typing a search term (or, now, picking any facet) while a run was building elsewhere in the
	// list STOPPED the poll, and that run never visibly advanced. It was already a live latent bug
	// when a search box was the only filter on the page; every facet added here would have widened
	// it. A filtered list that quietly stops updating during a build looks perfectly fine in a
	// screenshot, which is why AC-19 must be run by hand on prelive.
	//
	// WHAT DID NOT CHANGE: the stop condition (all-terminal or all-never-run still stops it —
	// AC-18), one refetch per tick at the LIST level and never per row (the one-round-trip rule of
	// AC-6/AC-21), and the poll staying SILENT (`loading={false}` below — AC-20).
	const shouldPollForRunStatus = hasNonTerminalPipelineRun(loadedRows);
	useInterval(() => refetch(), shouldPollForRunStatus ? PIPELINE_RUN_POLL_INTERVAL_MS : null);

	// AC-4..AC-8 — sortable columns, each with its null semantics stated EXPLICITLY.
	//
	// Every column carries its own `sortComparator`; not one of them falls back on the grid's
	// default. That is the spec's second-named regression risk ("sorting on a lie") — three of these
	// fields are nullable in ways a default comparator orders plausibly but wrongly, and a
	// plausible-but-wrong order is the kind of bug nobody reports.
	//
	// `disableColumnMenu` STAYS (AC-4: reconsider it "only so far as sorting requires" — it turns
	// out sorting requires nothing, because a header click sorts on its own). `disableColumnSelector`
	// stays too: a column chooser is out of scope.
	//
	// Multi-column sorting needs no suppressing: `disableMultipleColumnsSorting` is a FORCED prop on
	// the community DataGrid (verified in @mui/x-data-grid@5.17.11's DataGridForcedPropsKey), so a
	// shift-click cannot produce a two-key sort model. That is why the URL codec stores one
	// (field, direction) pair rather than a list.
	const columns = useMemo(
		() => [
			{
				field: 'Name',
				headerName: 'Pipeline',
				flex: 1,
				minWidth: 180,
				sortable: true,
				sortComparator: comparePipelineTextValues
			},
			{
				field: 'AppInstanceName',
				headerName: 'App Instance',
				flex: 1,
				minWidth: 180,
				sortable: true,
				// AC-5 — nulls sort LAST ascending (first descending, which the grid gets for free by
				// negating this comparator). Deliberately the OPPOSITE of Latest Run's null rule: a
				// missing owner is a data-integrity leftover, while a missing run is the most
				// actionable state on a worklist.
				sortComparator: compareAppInstanceNameValues,
				// AC-6 — a soft-deleted/absent owning App Instance resolves null server-side; render
				// the explicit `(deleted)` marker, never a bare em-dash and never an empty cell,
				// under every sort direction and every facet combination. The marker is shared with
				// the facet (constants/pipelineListNarrowing.js) so the value you can SELECT is
				// character-for-character the value you can SEE.
				renderCell: params => params.value || DELETED_APP_INSTANCE_MARKER
			},
			{
				field: 'ImageName',
				headerName: 'Source Image',
				flex: 1,
				minWidth: 180,
				sortable: true,
				sortComparator: comparePipelineTextValues
			},
			{
				field: 'ContainerAppName',
				headerName: 'Target Container',
				width: 180,
				sortable: true,
				sortComparator: comparePipelineTextValues
			},
			{
				field: 'LatestRunStatus',
				headerName: 'Latest Run',
				width: 160,
				sortable: true,
				// AC-7/AC-8 — ordered by the DECLARED status rank (never-run first ascending, then
				// Failed, Cancelled, the three in-flight states in lifecycle order, Succeeded last),
				// never alphabetically and never by the raw enum value. The rank and the reasoning
				// live together in constants/pipelineRunStatus.js.
				sortComparator: compareLatestRunStatusValues,
				// Bound straight from the payload; no per-row `runs` fetch. PipelineRunStatusChip
				// already renders the "No runs yet" empty state when status is null/undefined.
				renderCell: params => <PipelineRunStatusChip status={params.row.LatestRunStatus} />
			}
		],
		[]
	);

	// AC-4/AC-16 — the sort model is CONTROLLED from the URL, so a sort is shareable and survives
	// the detail round trip exactly like a facet does. An empty model means "no explicit sort",
	// which shows `loadedRows` in AC-1's default order.
	const sortModel = useMemo(() => toGridSortModel(viewState), [viewState]);

	const onSortModelChange = useCallback(
		nextModel => {
			updateViewState(fromGridSortModel(nextModel));
		},
		[updateViewState]
	);

	const handleRowClick = params => {
		// Browse-only row click always navigates into the detail page (VIEW), never RUN_VIEW/RUNS.
		// AC-25 — `deploy-pipelines-detail-flow` OQ-5/AC-19 ("the list is browse-only") still
		// stands: this slice adds NO row-level Run/Edit/Delete action.
		//
		// The list's current query string rides along in `location.state` (review LOW-3) so the detail
		// page's BREADCRUMB can return to the narrowed list, not a bare one — the back ARROW already
		// did, via navigate(-1), and the two sitting next to each other behaving differently was the
		// inconsistency. This is a per-navigation hand-off, NOT a second store of the narrowing: the
		// URL is still the only thing that decides what the list shows (D3 stands).
		navigate(ROUTE_STRING.PIPELINE.VIEW.replace(':id', params.row.Id), {
			state: { listSearch: searchString }
		});
	};

	// AC-14 — "no match" must be distinguishable from "no data", and must now cover the facets too,
	// not just the search box.
	const isNarrowed = isPipelineListNarrowed(viewState);

	return (
		<>
			<AppsHeader>
				<Box flex={1} display="flex" justifyContent="space-between" alignItems="center">
					<AppSearch
						isClearable
						searchValue={viewState.searchTerm}
						onClearSearch={clearSearchTerm}
						iconPosition="right"
						id="pipeline-search"
						size="small"
						overlap={false}
						placeholder="Search by App Instance, pipeline name, source image or target container…"
						onChange={onChangeSearch}
					/>
					<AllowedAccess permissionIds={[AuthorizePermission.PIPELINE_CREATE]}>
						<Button variant="outlined" onClick={() => navigate(ROUTE_STRING.PIPELINE.ADD)}>
							Create Pipeline
						</Button>
					</AllowedAccess>
				</Box>
			</AppsHeader>

			{/* AC-10/AC-12 — narrowing controls. Explicit controls feeding a pure predicate, NOT the
			    grid's own filter panel: the community edition's panel is single-field (multi-column
			    filter building is Pro), so it could not express "App Instance AND status" anyway, and
			    a predicate is unit-testable while the panel is not.

			    AC-23 — none of this is permission-gated. A PIPELINE_READ-only reader, very plausibly
			    most of the Infra persona, gets 100% of this slice's benefit. */}
			{!isLoading && totalPipelineCount > 0 && (
				<Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
					<Autocomplete
						multiple
						size="small"
						sx={{ minWidth: 280 }}
						options={appInstanceOptions}
						value={viewState.appInstances}
						// Free text is disabled: every option comes from the loaded rows, so a reader
						// cannot narrow to an App Instance that isn't on the page.
						onChange={(_e, selected) => updateViewState({ appInstances: selected })}
						renderInput={params => (
							<TextField
								{...params}
								label="App Instance"
								placeholder={viewState.appInstances.length ? '' : 'All App Instances'}
								inputProps={{ ...params.inputProps, 'aria-label': 'Filter by App Instance' }}
							/>
						)}
					/>

					{/* NFR-Accessibility — each quick-filter is a real <button> carrying `aria-pressed`,
					    so its on/off state is ANNOUNCED rather than conveyed by fill colour alone, and
					    it is keyboard-operable like any button. */}
					<Box role="group" aria-label="Filter by latest run status" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
						{[
							...Object.values(PipelineRunStatus).map(value => ({ value, label: PIPELINE_RUN_STATUS_LABEL[value] })),
							// AC-12 — never-run is an explicit, first-class option.
							{ value: NEVER_RUN_STATUS_VALUE, label: 'Never run' }
						].map(({ value, label }) => (
							<Chip
								key={String(value)}
								component="button"
								type="button"
								clickable
								size="small"
								label={label}
								aria-pressed={isStatusSelected(value)}
								variant={isStatusSelected(value) ? 'filled' : 'outlined'}
								color={isStatusSelected(value) ? 'primary' : 'default'}
								onClick={() => toggleStatus(value)}
							/>
						))}
					</Box>
				</Box>
			)}

			<Box sx={{ mt: 3 }}>
				{isLoading && <Skeleton variant="rounded" height={200} />}

				{!isLoading && totalPipelineCount === 0 && <Alert severity="info">No pipelines yet. Create one to build and deploy a container image on demand.</Alert>}

				{/* AC-14 — reworded from "match your search" so it covers the facets too. A SORT alone
				    never lands here: sorting hides nothing, so it can never empty the list.

				    ONE message, not a narrowed/not-narrowed pair (review LOW-2). Reaching this branch
				    at all PROVES narrowing is active: the loaded row count is always exactly
				    `totalPipelineCount` (flattenPipelineGroups maps 1:1 over the same Pipelines arrays
				    this sums), and `narrowPipelineRows` drops a row only when one of the three
				    narrowings is set — which is precisely `isPipelineListNarrowed`. The old `else` half
				    could therefore never render, and it still held the pre-reword string, so it read
				    like a live branch to the next person. */}
				{!isLoading && totalPipelineCount > 0 && visibleRows.length === 0 && <Alert severity="info">No pipelines match the current search and filters.</Alert>}

				{!isLoading && visibleRows.length > 0 && (
					// Every DataGrid list table must sit in a width:100% Box — flex children default to
					// min-width:auto and won't shrink below content, so an unwrapped grid sizes to
					// content and clips its last column.
					<Box sx={{ width: '100%' }}>
						<DataGrid
							hideFooter
							// `autoHeight` stays (spec D1 — no fixed height). Two independent reasons:
							// a fixed height would make Pipelines the portal's only inner-scrolling
							// table for an unmeasured render win, and the D7 spike measured that a
							// fixed-height grid renders only a 3-of-10-row stub under jsdom, which
							// would cost most of this slice's component-test coverage.
							autoHeight
							disableColumnSelector
							disableColumnMenu
							rows={visibleRows}
							columns={columns}
							getRowId={row => row.Id}
							sortingMode="client"
							sortModel={sortModel}
							onSortModelChange={onSortModelChange}
							// Initial load is already gated by the `!isLoading` guard above, so the grid
							// only mounts with data. Do NOT wire `isRefetching` here (AC-20): the
							// background poll refetches every PIPELINE_RUN_POLL_INTERVAL_MS while a run
							// is in flight, and surfacing that as the grid loading overlay would pulse
							// the whole table every few seconds during a deploy. Polling stays silent;
							// rows update in place.
							loading={false}
							onRowClick={handleRowClick}
							sx={{ cursor: 'pointer' }}
						/>
					</Box>
				)}
			</Box>

			<FooterWrapper className="footer">
				<Box justifyContent="center" className="footerContainer">
					<Tooltip title="Refresh">
						<Button size="small" onClick={() => refetch()}>
							Refresh
						</Button>
					</Tooltip>
					{/* The total is the LOADED count, unchanged by narrowing; the visible count is shown
					    alongside it only while a filter is actually active, so a reader can tell "3 of
					    41" from "41". */}
					<Typography sx={{ ml: 2 }}>
						{isNarrowed && visibleRows.length !== totalPipelineCount ? `Showing: ${visibleRows.length} of ${totalPipelineCount}` : `Total: ${totalPipelineCount}`}
					</Typography>
				</Box>
			</FooterWrapper>
		</>
	);
};

export default React.memo(PipelineTable);
